/**
 * The central Persian label dictionary for the canonical enums of API_CONTRACT §5 (FTD §3.4).
 * Screens never translate an enum themselves. An unknown (additive) value gets the neutral
 * {@link UNKNOWN_STATUS_LABEL} and is reported through the registered reporter (AC §2).
 *
 * Each entry also carries a semantic tone so badges use one mapping. Tone is never the only signal:
 * the label is always shown (FTD §2.6).
 */

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface StatusEntry {
  readonly labelFa: string;
  readonly tone: StatusTone;
}

type Dictionary = Readonly<Record<string, StatusEntry>>;

const s = (labelFa: string): StatusEntry => ({ labelFa, tone: 'success' });
const w = (labelFa: string): StatusEntry => ({ labelFa, tone: 'warning' });
const d = (labelFa: string): StatusEntry => ({ labelFa, tone: 'danger' });
const i = (labelFa: string): StatusEntry => ({ labelFa, tone: 'info' });
const n = (labelFa: string): StatusEntry => ({ labelFa, tone: 'neutral' });

export const statusDictionary = {
  saleStatus: {
    DRAFT: n('پیش‌نویس'),
    RESERVED: i('رزروشده'),
    WAITING_FOR_CONFIRMATION: w('در انتظار تأیید خریدار'),
    CONFIRMED: s('تأییدشده'),
    SETTLEMENT_STARTED: i('در حال تسویه'),
    COMPLETED: s('تکمیل‌شده'),
    EXPIRED: n('منقضی‌شده'),
    REJECTED: d('ردشده'),
    CANCELLED: n('لغوشده'),
    FAILED: d('ناموفق'),
  },
  installmentStatus: {
    UPCOMING: n('آینده'),
    DUE: w('سررسید امروز'),
    GRACE_PERIOD: w('در مهلت پرداخت'),
    DELINQUENT: d('معوق'),
    PAID: s('پرداخت‌شده'),
    CANCELLED: n('لغوشده'),
  },
  returnRequestStatus: {
    REQUESTED: i('ثبت‌شده'),
    WAITING_FOR_BUYER: w('در انتظار تأیید خریدار'),
    CONFIRMED: s('تأییدشده'),
    APPLIED: s('اعمال‌شده'),
    REJECTED: d('ردشده'),
    EXPIRED: n('منقضی‌شده'),
    INELIGIBLE: n('غیرقابل مرجوعی'),
  },
  returnType: {
    FULL: n('مرجوعی کامل'),
    PARTIAL: n('مرجوعی جزئی'),
  },
  confirmationRequestStatus: {
    CREATED: i('ایجادشده'),
    DELIVERED: w('در انتظار تصمیم'),
    CONFIRMED: s('تأییدشده'),
    REJECTED: d('ردشده'),
    EXPIRED: n('منقضی‌شده'),
    INVALIDATED: d('باطل‌شده'),
  },
  confirmationMode: {
    SMS_CODE_TO_MERCHANT: n('کد پیامکی به فروشنده'),
    IN_APP_APPROVAL: n('تأیید در برنامه'),
  },
  confirmationOperationType: {
    PURCHASE: n('خرید'),
    RETURN: n('مرجوعی'),
  },
  reservationStatus: {
    ACTIVE: w('رزرو فعال'),
    COMMITTED: s('قطعی‌شده'),
    RELEASED: n('آزادشده'),
    EXPIRED: n('منقضی‌شده'),
  },
  qrTokenStatus: {
    ACTIVE: s('فعال'),
    SCANNED: i('اسکن‌شده'),
    EXPIRED: n('منقضی‌شده'),
    INVALIDATED: n('باطل‌شده'),
  },
  paymentIntentStatus: {
    CREATED: i('ایجادشده'),
    AWAITING_GATEWAY: w('در انتظار درگاه پرداخت'),
    VERIFYING: w('در حال بررسی پرداخت'),
    SUCCEEDED: s('موفق'),
    FAILED: d('ناموفق'),
    CANCELLED: n('لغوشده'),
    EXPIRED: n('منقضی‌شده'),
  },
  paymentAttemptStatus: {
    INITIATED: i('شروع‌شده'),
    PENDING: w('در انتظار'),
    SUCCEEDED: s('موفق'),
    FAILED: d('ناموفق'),
    CANCELLED: n('لغوشده'),
    REVERSED: d('برگشت‌خورده'),
  },
  settlementBatchStatus: {
    BUILDING: i('در حال تشکیل'),
    FINALIZED: s('نهایی‌شده'),
    CLOSED: n('بسته‌شده'),
  },
  /** Settlement batch `batchKind` (AC §5, §34.2). */
  settlementBatchKind: {
    WEEKLY_INSTALLMENT: n('هفتگی اقساط'),
    DAILY_CASH: n('نقدی روزانه'),
  },
  payoutAttemptStatus: {
    PENDING: w('در انتظار'),
    PROCESSING: i('در حال پردازش'),
    SUCCEEDED: s('موفق'),
    FAILED: d('ناموفق'),
    REVERSED: d('برگشت‌خورده'),
  },
  approvalRequestStatus: {
    PENDING: w('در انتظار تأیید'),
    APPROVED: s('تأییدشده'),
    REJECTED: d('ردشده'),
    CANCELLED: n('لغوشده'),
    EXPIRED: n('منقضی‌شده'),
  },
  installmentPlanType: {
    END_OF_SALE_MONTH: n('پرداخت آخر ماه'),
    NUMBERED: n('اقساطی'),
  },
  buyerStatus: {
    PENDING_VERIFICATION: w('در انتظار تأیید هویت'),
    ACTIVE: s('فعال'),
    SUSPENDED: d('تعلیق‌شده'),
    CLOSED: n('بسته‌شده'),
  },
  merchantStatus: {
    PENDING_ACTIVATION: w('در انتظار فعال‌سازی'),
    ACTIVE: s('فعال'),
    SUSPENDED: d('تعلیق‌شده'),
    CLOSED: n('بسته‌شده'),
  },
  creditAllocationStatus: {
    SCHEDULED: i('زمان‌بندی‌شده'),
    ACTIVE: s('فعال'),
    EXPIRED: n('منقضی‌شده'),
    REVOKED: n('لغوشده'),
  },
  creditAllocationType: {
    BASE: n('اعتبار پایه'),
    ADDITIONAL: n('اعتبار تکمیلی'),
    WALLET: n('اعتبار کیف پول'),
  },
  cardAvailabilityStatus: {
    ACTIVE: s('فعال'),
    RESTRICTED: d('محدودشده'),
    NO_AVAILABLE_CREDIT: w('بدون اعتبار قابل استفاده'),
  },
  blockReason: {
    BUYER_SUSPENDED: d('حساب تعلیق شده است'),
    IDENTITY_NOT_VERIFIED: w('هویت هنوز تأیید نشده است'),
    DELINQUENCY: d('قسط معوق دارید'),
    GUARANTOR_DELINQUENCY: d('معوقه در ضمانت'),
    MANUAL_RESTRICTION: d('محدودیت از سوی پشتیبانی'),
    WRITTEN_OFF: d('بدهی سوخت‌شده'),
  },
  lateFeeModel: {
    NONE: n('بدون جریمه دیرکرد'),
    FIXED_AMOUNT: n('مبلغ ثابت'),
    FIXED_PERCENTAGE_OF_INSTALLMENT: n('درصد ثابت از قسط'),
    DAILY_WITH_CAP: n('روزانه با سقف'),
  },
  merchantLegalEntityType: {
    INDIVIDUAL: n('حقیقی'),
    LEGAL_ENTITY: n('حقوقی'),
  },
  buyerRestrictionType: {
    PURCHASE_BLOCK: d('مسدودی خرید'),
  },
  buyerRestrictionSourceType: {
    DELINQUENCY: d('معوقه'),
    GUARANTOR_DELINQUENCY: d('معوقه ضمانت'),
    MANUAL: n('دستی'),
    WRITE_OFF: d('سوخت مطالبات'),
  },
  buyerRestrictionStatus: {
    ACTIVE: d('فعال'),
    RELEASED: s('رفع‌شده'),
  },
  returnRejectionSource: {
    BUYER: n('خریدار'),
    MERCHANT: n('فروشنده'),
    SECURITY: d('امنیتی'),
  },
  creditProgramStatus: {
    DRAFT: n('پیش‌نویس'),
    PENDING_APPROVAL: w('در انتظار تأیید'),
    ACTIVE: s('فعال'),
    SUPERSEDED: n('جایگزین‌شده'),
    CLOSED: n('بسته‌شده'),
  },
  creditProgramTargetType: {
    INDIVIDUAL: n('فردی'),
    ORGANIZATION: n('سازمان'),
    ORGANIZATION_ROLE: n('نقش سازمانی'),
    GEOGRAPHY: n('جغرافیایی'),
  },
  targetingRunStatus: {
    PENDING_APPROVAL: w('در انتظار تأیید'),
    EXECUTING: i('در حال اجرا'),
    COMPLETED: s('تکمیل‌شده'),
    REJECTED: d('ردشده'),
    FAILED: d('ناموفق'),
  },
  enrollmentRequestStatus: {
    DRAFT: n('پیش‌نویس'),
    VALIDATED: i('بررسی‌شده'),
    PROVISIONING: i('در حال ایجاد حساب‌ها'),
    COMPLETED: s('تکمیل‌شده'),
    PARTIALLY_COMPLETED: w('تکمیل ناقص'),
    CANCELLED: n('لغوشده'),
  },
  enrollmentMemberRowStatus: {
    PENDING: n('در انتظار'),
    VALID: s('معتبر'),
    INVALID: d('نامعتبر'),
    PROVISIONED: s('ایجادشده'),
    LINKED_EXISTING_BUYER: s('متصل به خریدار موجود'),
    FAILED: d('ناموفق'),
    EXCLUDED: n('کنارگذاشته‌شده'),
  },
  /** TargetingExclusionCode (AC §18.9.1). */
  targetingExclusionCode: {
    ALREADY_HOLDS_PROGRAM: n('از قبل دارای این برنامه'),
    BUYER_BLOCKED: w('خریدار محدود یا تعلیق‌شده'),
    BUYER_NOT_ACTIVE: n('خریدار غیرفعال'),
    MEMBERSHIP_INACTIVE: n('عضویت غیرفعال'),
    BUYER_NOT_FOUND: n('خریدار پیدا نشد'),
    CREDIT_POLICY_LIMIT: w('عبور از سقف سیاست اعتبار'),
  },
  /** ValidityPolicy `mode` (AC §4.7). */
  validityMode: {
    DURATION_FROM_ASSIGNMENT: n('مدت از زمان تخصیص'),
    FIXED_WINDOW: n('بازه تاریخ ثابت'),
    NON_EXPIRING: n('بدون انقضا'),
  },
  /** AdminBuyer.provisioningMethod (AC §18.6.2). */
  buyerProvisioningMethod: {
    ADMIN: n('ثبت توسط ادمین'),
    ORGANIZATION_ENROLLMENT: n('ثبت‌نام سازمانی'),
  },
  /** FinancialAdjustmentRequest.direction (AC §18.11). */
  financialAdjustmentDirection: {
    INCREASE: n('افزایش مانده'),
    DECREASE: n('کاهش مانده'),
  },
  approvalActionType: {
    BUYER_CREDIT_CHANGE: n('تغییر اعتبار خریدار'),
    CREDIT_ALLOCATION_ADJUSTMENT: n('اصلاح تخصیص اعتبار'),
    MERCHANT_CAPACITY_CHANGE: n('تغییر ظرفیت فروشگاه'),
    MERCHANT_FEE_CHANGE: n('تغییر کارمزد فروشگاه'),
    PAYOUT_ACCOUNT_CHANGE: n('تغییر حساب تسویه'),
    WRITE_OFF: n('سوخت مطالبات'),
    CREDIT_PROGRAM_ACTIVATION: n('فعال‌سازی برنامه اعتباری'),
    CREDIT_PROGRAM_TARGETING_RUN: n('اجرای هدف‌گذاری برنامه اعتباری'),
    MERCHANT_RECOVERY_CLAIM: n('مطالبه بازیافت از فروشگاه'),
    MERCHANT_RECOVERY_WAIVER: n('بخشودگی بازیافت فروشگاه'),
    FINANCIAL_ADJUSTMENT: n('اصلاح مالی'),
    PLATFORM_ROLE_GRANT: n('دادن نقش پلتفرم'),
    WALLET_CREDIT_POLICY_CHANGE: n('تغییر سیاست اعتبار کیف پول'),
  },
  settlementHoldType: {
    MANUAL: n('دستی'),
    PAYOUT_ACCOUNT_CHANGE: n('تغییر حساب تسویه'),
  },
  settlementHoldStatus: {
    ACTIVE: w('فعال'),
    RELEASED: s('آزادشده'),
  },
  /** Settlement line `lineKind` (AC §30.25). */
  settlementLineKind: {
    INSTALLMENT: n('قسط'),
    WALLET_CASH: n('نقدی'),
  },
  /** Settlement line status (AC §5, §26.4). */
  settlementLineStatus: {
    PENDING: w('در انتظار'),
    HELD: w('نگه‌داشته‌شده'),
    LOCKED: i('قفل‌شده'),
    PAID: s('پرداخت‌شده'),
    CANCELLED: n('لغوشده'),
  },
  /** Cash sale `feeSource` (AC §5, §34.5). Admin only: no merchant or buyer screen shows it. */
  cashSaleFeeSource: {
    MERCHANT: n('نرخ خود فروشگاه'),
    GLOBAL_DEFAULT: n('پیش‌فرض سیستم'),
  },
  /** Cash sale `payoutStatus` (AC §5, §34.7.4): `SCHEDULED`, then the line statuses. */
  cashSalePayoutStatus: {
    SCHEDULED: i('در صف واریز'),
    PENDING: w('در انتظار'),
    HELD: w('نگه‌داشته‌شده'),
    LOCKED: i('قفل‌شده'),
    PAID: s('پرداخت‌شده'),
  },
  walletFreezeStatus: {
    HELD: w('نگه‌داشته‌شده'),
    FROZEN: w('مسدودشده'),
    SETTLED: s('تسویه‌شده'),
    CANCELLED: n('لغوشده'),
  },
  walletEntryType: {
    TOP_UP: s('شارژ کیف پول'),
    PURCHASE_HOLD: w('نگه‌داشت خرید'),
    PURCHASE_HOLD_RELEASED: s('آزادسازی نگه‌داشت خرید'),
    PURCHASE_FREEZE: w('مسدودسازی خرید'),
    FREEZE_RELEASE: s('آزادسازی مبلغ'),
    INSTALLMENT_COLLECTION: n('برداشت قسط'),
    INSTALLMENT_PAYMENT: s('پرداخت قسط'),
    CASH_SALE_PAYMENT: n('پرداخت نقدی'),
    TRANSFER_SENT: n('انتقال وجه'),
    TRANSFER_RECEIVED: s('دریافت وجه'),
    WITHDRAWAL_REQUESTED: w('درخواست برداشت'),
    WITHDRAWAL_CANCELLED: n('لغو برداشت'),
    WITHDRAWAL_RETURNED: s('بازگشت برداشت'),
    REFUND: s('بازگشت وجه'),
  },
  /** Merchant recovery entry type (AC §5, §26.8). */
  merchantRecoveryEntryType: {
    CLAIM: n('مطالبه'),
    OFFSET: n('کسر از تسویه'),
    WAIVER: n('بخشودگی'),
  },
  /** Derived merchant recovery status (AC §5, §26.8). */
  merchantRecoveryStatus: {
    OPEN: w('باز'),
    PARTIALLY_RECOVERED: i('بازیافت جزئی'),
    RECOVERED: s('بازیافت‌شده'),
  },
  reconciliationItemType: {
    PAYMENT_UNMATCHED: n('پرداخت تطبیق‌نیافته'),
    PAYMENT_AFTER_CANCELLATION: n('پرداخت پس از لغو'),
    PAYMENT_DUPLICATE: n('پرداخت تکراری'),
    PROVIDER_AMOUNT_MISMATCH: n('مغایرت مبلغ با ارائه‌دهنده'),
    PAYOUT_UNCONFIRMED: n('واریز تأییدنشده'),
    PAYOUT_RETURNED: n('واریز برگشتی'),
    WALLET_TOP_UP_OVER_CAP: n('شارژ کیف پول بیش از سقف'),
  },
  reconciliationItemStatus: {
    OPEN: w('باز'),
    RESOLVED: s('رفع‌شده'),
  },
  calendarSyncRunStatus: {
    RUNNING: i('در حال اجرا'),
    REVIEW_PENDING: w('در انتظار بازبینی'),
    APPLIED: s('اعمال‌شده'),
    FAILED: d('ناموفق'),
  },
  payoutAccountOwnerType: {
    MERCHANT: n('فروشگاه'),
    MARKETING_RECIPIENT: n('بازاریاب'),
    BUYER: n('خریدار'),
  },
  payoutAccountVerificationStatus: {
    PENDING: w('در انتظار بررسی'),
    VERIFIED: s('تأییدشده'),
    REJECTED: d('ردشده'),
  },
  businessCalendarSource: {
    OFFICIAL_SYNC: n('همگام‌سازی رسمی'),
    MANUAL: n('دستی'),
  },
  fundingSource: {
    SAAY_CAPITAL: n('سرمایه SAAY'),
  },
  buyerDevicePlatform: {
    ANDROID: n('اندروید'),
    IOS: n('iOS'),
    WEB: n('وب'),
  },
  // Stage 2 setup enums (AC §17.5), promoted from admin-web.
  /** MerchantUserAssignment.status (AC §17.5.2). */
  merchantUserStatus: { ACTIVE: s('فعال'), DISABLED: n('غیرفعال'), INVITED: i('دعوت‌شده') },
  /** ApprovalRequestSummary.targetType (AC §18.2.7): a string, not an enum; unknown values are neutral. */
  approvalTargetType: {
    BUYER: n('خریدار'),
    CREDIT_ALLOCATION: n('تخصیص اعتبار'),
    MERCHANT: n('فروشگاه'),
    PAYOUT_OWNER: n('صاحب حساب تسویه'),
    CREDIT_PROGRAM: n('برنامه اعتباری'),
    POLICY_TYPE: n('سیاست'),
    INSTALLMENT: n('قسط'),
    WRITE_OFF_REQUEST: n('درخواست سوخت'),
    MERCHANT_RECOVERY_ENTRY: n('بازیافت فروشگاه'),
    IDENTITY_USER: n('کاربر'),
  },
  /** `APPROVAL_SELF_DECISION_FORBIDDEN` `details.reason` (AC §18.2.4, §18.20.4). */
  approvalSelfDecisionReason: {
    SELF_REQUEST: w('این درخواست را خودتان ثبت کرده‌اید.'),
    LINKED_IDENTITY: w('شما به موضوع این درخواست مرتبط هستید.'),
    GRANTED_BY_REQUESTER: w('نقش تصمیم‌گیری شما را ثبت‌کنندهٔ همین درخواست داده است.'),
    OWN_TARGET: w('موضوع درخواست، پروفایل یا فروشگاه خود ثبت‌کننده است.'),
  },
  /** BranchRequest.status and .requestType (AC §18.4.6). */
  branchRequestStatus: {
    PENDING: w('در انتظار بررسی'),
    APPROVED: s('اعمال‌شده'),
    REJECTED: d('ردشده'),
  },
  branchRequestType: {
    CREATE: n('شعبهٔ جدید'),
    UPDATE: n('ویرایش شعبه'),
    DEACTIVATE: n('غیرفعال‌سازی شعبه'),
  },
  /** CommercialSettingVersion.status and ConfirmationModeOverride.status (AC §18.4.8). */
  commercialSettingStatus: {
    SCHEDULED: i('زمان‌بندی‌شده'),
    EFFECTIVE: s('جاری'),
    SUPERSEDED: n('جایگزین‌شده'),
  },
  /** PayoutAccountChangeRequest.status (AC §18.5). */
  payoutChangeRequestStatus: {
    PENDING: w('در انتظار بررسی'),
    APPROVAL_PENDING: i('در انتظار تأیید'),
    APPLIED: s('اعمال‌شده'),
    REJECTED: d('ردشده'),
  },
  /** LoginLockClearance.status (AC §18.21). */
  loginLockClearanceStatus: {
    REQUESTED: i('در حال انجام'),
    COMPLETED: s('انجام‌شده'),
    FAILED: d('ناموفق'),
  },
  /** AdminAuditEvent.domain (AC §18.12, M-17). */
  auditDomain: {
    FINANCIAL: n('مالی'),
    OPERATIONS: n('عملیاتی'),
    MARKETING: n('بازاریابی'),
    PLATFORM: n('دسترسی و پلتفرم'),
  },
  /** PlatformRoleAssignment.status (AC §17.4.5, §18.12). */
  platformRoleAssignmentStatus: { ACTIVE: s('فعال'), REVOKED: n('لغوشده') },
  /** The identity of a platform role assignment (AC §18.12 `user.status`). */
  identityUserStatus: {
    PENDING_PROVISIONING: i('در انتظار ایجاد حساب'),
    ACTIVE: s('فعال'),
    SUSPENDED: w('تعلیق‌شده'),
    CLOSED: n('بسته‌شده'),
  },
  /** PolicyVersion.status (AC §17.5.3). */
  policyVersionStatus: {
    EFFECTIVE: s('جاری'),
    SCHEDULED: i('زمان‌بندی‌شده'),
    SUPERSEDED: n('جایگزین‌شده'),
  },
  /** InstallmentPlanActivation.status (AC §17.5.4). */
  planActivationStatus: {
    EFFECTIVE: s('فعال'),
    ENDED: n('پایان‌یافته'),
    SCHEDULED: i('زمان‌بندی‌شده'),
  },
  /** GeographyArea / MerchantCategory / MerchantBranch status. */
  activeStatus: { ACTIVE: s('فعال'), INACTIVE: n('غیرفعال') },
  /** MerchantActivationRequirement (AC §17.5.2), in the contract's enum order. */
  activationRequirement: {
    KYC: w('تأیید هویت فروشگاه'),
    PRIMARY_CATEGORY: w('دسته‌بندی اصلی'),
    COMMERCIAL_SETTING: w('کارمزد و تنظیمات تجاری'),
    CAPACITY: w('ظرفیت فروش'),
    INSTALLMENT_PLAN: w('طرح اقساطی فعال'),
    ACTIVE_BRANCH: w('شعبه فعال'),
    MERCHANT_ADMIN: w('مدیر فروشگاه'),
    VERIFIED_PAYOUT_ACCOUNT: w('حساب تسویه تأییدشده'),
  },
  /** Policy types (the `{policyType}` path values, AC §17.5.3). */
  policyType: {
    'installment-bounds': n('محدوده اقساط'),
    confirmation: n('تأیید خرید'),
    delinquency: n('مهلت و جریمه دیرکرد'),
    'late-fee': n('مدل‌های مجاز جریمه دیرکرد'),
    'payment-fee': n('کارمزد پرداخت'),
    'cash-sale-fee': n('کارمزد فروش نقدی'),
    'physical-card-numbering': n('کارت فیزیکی'),
    'buyer-provisioning': n('ثبت خریدار'),
    guarantee: n('ضمانت'),
    credit: n('سقف اعتبار'),
    approval: n('تأیید دومرحله‌ای'),
    wallet: n('کیف پول'),
    'wallet-credit': n('اعتبار کیف پول'),
    'wallet-top-up-fee': n('کارمزد شارژ کیف پول'),
  },
  /** Identity document type codes (buyer-provisioning policy `acceptedIdentityDocumentTypes`). */
  identityDocumentType: { NATIONAL_CARD: n('کارت ملی') },
  /** AdminBuyer.identityVerification.method. */
  identityVerificationMethod: {
    ID_DOCUMENT: n('بررسی مدرک شناسایی'),
    ORGANIZATION_EVIDENCE: n('مدرک سازمانی'),
  },
  /** AdminMerchant.confirmationMode.source. */
  confirmationModeSource: {
    MERCHANT_OVERRIDE: n('تنظیم ویژه فروشگاه'),
    PLATFORM_DEFAULT: n('پیش‌فرض پلتفرم'),
  },
  /** Money timeline actor (AC §30.28). */
  moneyActorType: {
    BUYER: n('خریدار'),
    MERCHANT_USER: n('کاربر فروشگاه'),
    PLATFORM_ADMIN: n('مدیر پلتفرم'),
    SYSTEM: n('سامانه'),
  },
  /** Money timeline `kind` (AC §30.28). */
  moneyTimelineKind: {
    PURCHASE: n('خرید'),
    INSTALLMENT_PAYMENT: n('پرداخت قسط'),
    TOP_UP: n('شارژ کیف پول'),
    WITHDRAWAL: n('برداشت'),
    TRANSFER_SENT: n('انتقال به دیگران'),
    TRANSFER_RECEIVED: n('دریافت انتقال'),
    REFUND: n('بازگشت وجه'),
    WALLET_FREEZE: n('بلوکه برای خرید'),
    WALLET_RELEASE: n('آزاد شدن بلوکه'),
    MULTIPLIER_OVERRIDE: n('ضریب اعتبار'),
  },
  /** Installment payment `source` on the money timeline (AC §30.28). */
  moneyTimelinePaymentSource: {
    GATEWAY: n('درگاه'),
    WALLET: n('کیف پول'),
    WALLET_COLLECTION: n('برداشت خودکار قسط'),
  },
  /** Where the buyer's effective `k` comes from (AC §30.28). Admins only. */
  multiplierSource: {
    POLICY: n('سیاست سراسری'),
    BUYER_OVERRIDE: n('ضریب اختصاصی'),
  },
  /** Purchase `paymentType` (AC §30.25). */
  salePaymentType: {
    INSTALLMENT: n('اقساطی'),
    WALLET_CASH: n('نقدی'),
  },
  /** Wallet top-up status (AC §30.9). */
  walletTopUpStatus: {
    CREATED: i('ثبت‌شده'),
    AWAITING_GATEWAY: w('در انتظار درگاه'),
    SUCCEEDED: s('موفق'),
    FAILED: d('ناموفق'),
    CANCELLED: n('لغوشده'),
    EXPIRED: n('منقضی‌شده'),
  },
  /** Payment and payout providers shown on wallet screens. */
  walletProvider: {
    SANDBOX: n('درگاه آزمایشی'),
    BEHPARDAKHT: n('به‌پرداخت ملت'),
    SEP: n('پرداخت الکترونیک سامان'),
    SADAD: n('پرداخت الکترونیک سداد'),
    PEC: n('تجارت الکترونیک پارسیان'),
    IRAN_KISH: n('کارت اعتباری ایران‌کیش'),
    PASARGAD: n('پرداخت الکترونیک پاسارگاد'),
    ZARINPAL: n('زرین‌پال'),
    IDPAY: n('آیدی‌پی'),
  },
  walletReconciliationStatus: {
    OPEN: w('باز'),
    RESOLVED: s('حل‌شده'),
  },
  fixedRole: {
    SUPER_ADMIN: n('مدیر ارشد'),
    FINANCE_ADMIN: n('مدیر مالی'),
    OPERATIONS_ADMIN: n('مدیر عملیات'),
    MARKETING_ADMIN: n('مدیر بازاریابی'),
    AUDITOR: n('حسابرس'),
    MARKETER: n('بازاریاب'),
    MERCHANT_ADMIN: n('مدیر فروشگاه'),
    FINANCE_MANAGER: n('مدیر مالی فروشگاه'),
    SELLER: n('فروشنده'),
    COURIER: n('فروشنده سیار'),
    BUYER: n('خریدار'),
  },
} as const satisfies Readonly<Record<string, Dictionary>>;

export type StatusEnum = keyof typeof statusDictionary;

/** The known values of one canonical enum, for example `StatusValue<'saleStatus'>`. */
export type StatusValue<E extends StatusEnum> = keyof (typeof statusDictionary)[E] & string;

export const UNKNOWN_STATUS_LABEL = 'نامشخص';

export type UnknownStatusReporter = (enumName: StatusEnum, value: string) => void;

let reporter: UnknownStatusReporter | undefined;

/**
 * Registers the monitoring hook for unknown enum values (FTD §3.4). Only the enum name and the raw
 * value are passed; never user data.
 */
export function setUnknownStatusReporter(next: UnknownStatusReporter | undefined): void {
  reporter = next;
}

export interface ResolvedStatus extends StatusEntry {
  readonly known: boolean;
  readonly value: string;
}

/** Resolves an enum value to its label and tone; unknown values get a neutral generic label. */
export function resolveStatus(enumName: StatusEnum, value: string): ResolvedStatus {
  const dictionary: Dictionary = statusDictionary[enumName];
  const entry = Object.prototype.hasOwnProperty.call(dictionary, value)
    ? dictionary[value]
    : undefined;

  if (entry === undefined) {
    reporter?.(enumName, value);
    return { known: false, labelFa: UNKNOWN_STATUS_LABEL, tone: 'neutral', value };
  }

  return { ...entry, known: true, value };
}

/** The Persian label of an enum value (neutral generic label when unknown). */
export function statusLabel(enumName: StatusEnum, value: string): string {
  return resolveStatus(enumName, value).labelFa;
}

/** The known values of an enum in contract order. */
export function statusValues<E extends StatusEnum>(enumName: E): StatusValue<E>[] {
  return Object.keys(statusDictionary[enumName]) as StatusValue<E>[];
}
